import i18n from '@/i18n';

// All match times surface to NY/NJ users. Storage is UTC; display is ET.
// IANA name auto-handles EST↔EDT around DST.
const TZ = 'America/New_York';

// The active locale for Intl date formatting. Reads the live i18next language
// so weekday/month *words* localize. The dev pseudo-locale 'en-XA' isn't a real
// BCP-47 tag Intl understands, so collapse it to 'en'.
function displayLocale(): string {
  const lng = i18n.language;
  return lng === 'en-XA' ? 'en' : lng;
}

export type MatchGroup = 'LIVE' | 'TODAY' | 'WEEK' | 'LATER' | 'PAST';

export function nowDate(): Date {
  return new Date();
}

// "18:00" — 24hr matches the mockup aesthetic and keeps row badges compact.
// Timezone is implicit (audience is NY/NJ; header shows "NY/NJ").
export function formatKickoffTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(displayLocale(), {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// "Wed, Jun 11"
export function formatKickoffDate(iso: string): string {
  return new Date(iso).toLocaleDateString(displayLocale(), {
    timeZone: TZ,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// "Wed, Jun 11 · 7:00 PM ET"
export function formatKickoffFull(iso: string): string {
  return `${formatKickoffDate(iso)} · ${formatKickoffTime(iso)}`;
}

// "Thursday · May 7" (editorial header)
export function formatTodayLong(ref: Date = nowDate()): string {
  const weekday = ref.toLocaleDateString(displayLocale(), { timeZone: TZ, weekday: 'long' });
  const monthDay = ref.toLocaleDateString(displayLocale(), { timeZone: TZ, month: 'long', day: 'numeric' });
  return `${weekday} · ${monthDay}`;
}

// "Thu · May 7" (minimal header)
export function formatTodayShort(ref: Date = nowDate()): string {
  const weekday = ref.toLocaleDateString(displayLocale(), { timeZone: TZ, weekday: 'short' });
  const monthDay = ref.toLocaleDateString(displayLocale(), { timeZone: TZ, month: 'short', day: 'numeric' });
  return `${weekday} · ${monthDay}`;
}

// YYYY-MM-DD in ET — used for same-day comparisons across timezones.
function etYMD(d: Date): string {
  // en-CA reliably formats as YYYY-MM-DD.
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

export function isToday(iso: string, ref: Date = nowDate()): boolean {
  return etYMD(new Date(iso)) === etYMD(ref);
}

// True if `iso` falls on the ET calendar day immediately before `ref`.
// Used by HomeScreen to surface yesterday's FT results alongside today's
// schedule — covers people checking the app the morning after a match.
export function isYesterday(iso: string, ref: Date = nowDate()): boolean {
  const yesterday = new Date(ref.getTime() - 24 * 60 * 60 * 1000);
  return etYMD(new Date(iso)) === etYMD(yesterday);
}

// Bucket items by their ET calendar day, sorted ascending. Each bucket gets
// a "Sat, Jun 13"-style label derived from the day's first item.
export function groupByDay<T extends { kickoffAt: string }>(
  items: T[],
): { label: string; items: T[] }[] {
  const map = new Map<string, T[]>();
  for (const it of items) {
    const key = etYMD(new Date(it.kickoffAt));
    const list = map.get(key);
    if (list) list.push(it);
    else map.set(key, [it]);
  }
  return [...map.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([, group]) => ({
      label: formatKickoffDate(group[0]!.kickoffAt),
      items: group,
    }));
}

export function groupMatch(
  match: { kickoffAt: string; live: boolean },
  ref: Date = nowDate(),
): MatchGroup {
  if (match.live) return 'LIVE';
  const kickoff = new Date(match.kickoffAt);
  if (kickoff.getTime() < ref.getTime()) return 'PAST';
  if (isToday(match.kickoffAt, ref)) return 'TODAY';
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  if (kickoff.getTime() - ref.getTime() <= SEVEN_DAYS_MS) return 'WEEK';
  return 'LATER';
}
